import { getMobileHostBridge } from "@/utils/mobile-host-bridge";

/**
 * The embedded-mode contract with the mobile app for a fresh access token.
 *
 * The WebView never holds a refresh token. When its access token is about to
 * expire the page posts `HOST_SESSION_REFRESH_REQUEST`; the app refreshes its
 * own session, writes the new access token into the `access_token` cookie (the
 * same script it runs before opening the page) and dispatches
 * `HOST_SESSION_REFRESHED_EVENT`, or `HOST_SESSION_REFRESH_FAILED_EVENT`.
 */
export const HOST_SESSION_REFRESH_REQUEST = "mela:session-refresh";
export const HOST_SESSION_REFRESHED_EVENT = "mela:session-refreshed";
export const HOST_SESSION_REFRESH_FAILED_EVENT = "mela:session-refresh-failed";
// Behind the app lock the host can refresh only after the user unlocks, so the
// wait has to cover the unlock itself. Only an app with the message handler
// gets this far, and it always answers: the timeout guards against a hung host.
export const HOST_SESSION_REFRESH_TIMEOUT_MS = 120_000;

export interface HostSession {
  /**
   * Asks the host app for a fresh access token. Resolves `true` once the host
   * reports it is in storage, `false` when there is no host to ask, it failed
   * or it did not answer in time.
   */
  refresh(): Promise<boolean>;
}

export const mobileHostSession: HostSession = {
  refresh: () =>
    new Promise<boolean>((resolve) => {
      const bridge = getMobileHostBridge();
      // An app version without the message handler falls back to an expiry.
      if (!bridge) {
        resolve(false);
        return;
      }

      let timeoutId = 0;
      const onRefreshed = () => settle(true);
      const onFailed = () => settle(false);
      const settle = (refreshed: boolean) => {
        window.clearTimeout(timeoutId);
        window.removeEventListener(HOST_SESSION_REFRESHED_EVENT, onRefreshed);
        window.removeEventListener(HOST_SESSION_REFRESH_FAILED_EVENT, onFailed);
        resolve(refreshed);
      };

      window.addEventListener(HOST_SESSION_REFRESHED_EVENT, onRefreshed);
      window.addEventListener(HOST_SESSION_REFRESH_FAILED_EVENT, onFailed);
      timeoutId = window.setTimeout(
        () => settle(false),
        HOST_SESSION_REFRESH_TIMEOUT_MS,
      );

      try {
        bridge.postMessage(
          JSON.stringify({ type: HOST_SESSION_REFRESH_REQUEST }),
        );
      } catch {
        settle(false);
      }
    }),
};
